import { useMemo, useCallback } from 'react';
import { useLocation, useParams, Params } from 'react-router-dom';
import { NavigationParams, FiltersParams } from 'types/params';
import { FilterName, TabNames } from 'types/enums';
import { useSearchController } from '../SearchContext';

const arrayStateFromSplittedParams = (key: string, params: URLSearchParams) => {
    const value = params.get(key);

    const arrayFromValue = value?.split(',');

    const state = [...new Set(arrayFromValue)];

    return state.length ? state : null;
};

const arrayStateFromParams = (
    key: string,
    params: URLSearchParams,
    paramsFromRoute?: string,
) => {
    const arrayFromParams = paramsFromRoute
        ? [...params.getAll(key), paramsFromRoute]
        : params.getAll(key);

    const state = [...new Set(arrayFromParams)];

    return state.length ? state : null;
};

const addRouteParamsToSearchParams = (
    urlSearchParams: URLSearchParams,
    routeParams: Readonly<Params<string>>,
) => {
    const searchParams = new URLSearchParams(urlSearchParams);

    const instrumentFromRoute =
        routeParams[FilterName.Instruments]?.toLowerCase();
    const genreFromRoute = routeParams[FilterName.Genres]?.toLowerCase();
    const sfxFromRoute = routeParams[FilterName.Sfxs]?.toLowerCase();

    if (instrumentFromRoute) {
        const isInstrumentFromRouteFound = searchParams
            .getAll(FilterName.Instruments)
            .some((value) => value === instrumentFromRoute);

        !isInstrumentFromRouteFound &&
            searchParams.append(
                FilterName.Instruments.toLowerCase(),
                instrumentFromRoute,
            );
    }

    if (genreFromRoute) {
        const isGenreFromRouteFound = searchParams
            .getAll(FilterName.Genres)
            .some((value) => value === genreFromRoute);

        !isGenreFromRouteFound &&
            searchParams.append(
                FilterName.Genres.toLowerCase(),
                genreFromRoute,
            );
    }

    if (sfxFromRoute) {
        const isSfxFromRouteFound = searchParams
            .getAll(FilterName.Sfxs)
            .some((value) => value === sfxFromRoute);

        !isSfxFromRouteFound &&
            searchParams.append(FilterName.Sfxs.toLowerCase(), sfxFromRoute);
    }

    return searchParams;
};

const getLowerCaseSearchParams = (search: string) => {
    const params = new URLSearchParams(search);
    const lowerCaseParams = new URLSearchParams();

    // make sure we work with lowerCase params
    for (const [name, value] of params) {
        lowerCaseParams.append(name.toLowerCase(), value);
    }

    return lowerCaseParams;
};

const getSearchParams = (
    search: string,
    routeParams: Readonly<Params<string>>,
) => {
    const lowerCaseParams = getLowerCaseSearchParams(search);

    const searchParams = addRouteParamsToSearchParams(
        lowerCaseParams,
        routeParams,
    );

    return searchParams;
};

const getSearchStateFromSearchParams = (
    search: string,
    routeParams: Readonly<Params<string>>,
    defaultTab: TabNames,
) => {
    const searchParams = getSearchParams(search, routeParams);

    const tab = searchParams.get(NavigationParams.tab) ?? defaultTab;

    const pageFromParams = searchParams.get(NavigationParams.page);
    const page = pageFromParams ? parseInt(pageFromParams, 10) : 1;

    const genres = arrayStateFromParams(FiltersParams.genres, searchParams);
    const instruments = arrayStateFromParams(
        FiltersParams.instruments,
        searchParams,
    );
    const sfxs = arrayStateFromParams(FiltersParams.sfxs, searchParams);
    const bpmlowerbound = searchParams.get(FiltersParams.bpmlowerbound);
    const bpmupperbound = searchParams.get(FiltersParams.bpmupperbound);
    const keyquality = searchParams.get(FiltersParams.keyquality);
    const keyrootnote = searchParams.get(FiltersParams.keyrootnote);
    const orderby = searchParams.get(FiltersParams.orderby);
    const orderbydirection = searchParams.get(FiltersParams.orderbydirection);
    const tags = arrayStateFromParams(FiltersParams.tags, searchParams);
    const textsearch = arrayStateFromSplittedParams(
        FiltersParams.textsearch,
        searchParams,
    );
    const type = searchParams.get(FiltersParams.type);

    const hasActiveFilters =
        !!genres?.length ||
        !!instruments?.length ||
        !!sfxs?.length ||
        !!bpmlowerbound ||
        !!bpmupperbound ||
        !!keyquality ||
        !!keyrootnote ||
        !!tags?.length ||
        !!textsearch?.length ||
        !!type;

    return {
        bpmlowerbound,
        bpmupperbound,
        page,
        genres,
        instruments,
        keyquality,
        keyrootnote,
        orderby,
        orderbydirection,
        sfxs,
        tab: tab.toLowerCase(),
        tags,
        textsearch,
        type,
        hasActiveFilters,
    };
};

export const useGetCurrentSearchParams = () => {
    const routeParams = useParams();

    const getCurrentSearchParams = useCallback(() => {
        return getSearchParams(window.location.search, routeParams);
    }, [routeParams]);

    return getCurrentSearchParams;
};

export const useCurrentSearchState = () => {
    const { defaultTab } = useSearchController();
    const { search } = useLocation();
    const routeParams = useParams();

    const currentSearchState = useMemo(
        () => getSearchStateFromSearchParams(search, routeParams, defaultTab),
        [search, routeParams, defaultTab],
    );

    return currentSearchState;
};
